import React, { useState, useEffect, forwardRef } from 'react';
import './Sidebar.css';
import { MenuList, MenuGroups } from './Menu';
import axios from 'axios';
import * as endPoints from '../../Constants/Endpoints';
import { useNavigate, useLocation } from 'react-router-dom';
import { logout } from "../../Authentication Services/AuthService";
import { IoLogOut, IoLockClosed } from "react-icons/io5";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from 'react-i18next';
import { GetConfig } from '../../Authentication Services/AuthService';
import brandMark from '../../assets/brand-mark.png';

const Sidebar = forwardRef(({ isOpen, menuState, loggingOut }, ref) => {
  const { t } = useTranslation('sidebar');
  const navigate = useNavigate();
  const location = useLocation();
  const [activeItem, setActiveItem] = useState('Dashboard');
  const [config, setConfig] = useState();

  useEffect(() => {
    GetConfigWrapper();
  }, []);

  useEffect(() => {
    setActiveItem(
      MenuList.find(x => "/" + x.to === location.pathname)?.title ?? ""
    );
  }, [location.pathname]);

  const hasMiniSiteAccess = config?.features?.miniSite;

  const isItemLocked = (item) => {
    if (item.to === 'manage-site') {
      return !hasMiniSiteAccess;
    }
    return false;
  };

  const menuClick = async (option) => {

    if (isItemLocked(option)) {
      return;
    }

    setActiveItem(option.title);

    try {
      const res = await axios.get(
        process.env.REACT_APP_API_SERVICE_BASE_URL + endPoints.Authenticated,
        { withCredentials: true }
      );

      if (option.to === "logout")
      {
        logoutClicked()
        return;
      }

      if (res.status === 200) {
        navigate("/" + option.to);
      }

      menuState(false);
    } catch (e) {
      navigate(0);
    }
  };

  function GetConfigWrapper() {
    GetConfig()
      .then((res) => {
        setConfig(res.data);
      })
      .catch(() => {});
  }

  function logoutClicked() {
    loggingOut(true);
    logout()
      .then(() => {
        navigate('/login');
      })
      .catch(() => {
        navigate("/login");
      })
      .finally(() => {
        loggingOut(false);
      });
  }

  const renderLockedIcon = () => (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id="mini-site-locked-tooltip">
          {t('sidebar.tooltips.miniSiteLocked')}
        </Tooltip>
      }
    >
      <span className="menu-lock" aria-label={t('sidebar.tooltips.miniSiteLocked')}>
        <IoLockClosed />
      </span>
    </OverlayTrigger>
  );

  const renderItem = (item, index) => {
    const locked = isItemLocked(item);
    return (
      <div
        key={index}
        className={`sidebar-item ${activeItem === item.title ? 'active' : ''} ${locked ? 'locked' : ''}`}
        onClick={() => menuClick(item)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); menuClick(item); } }}
        title={
          !isOpen
            ? locked
              ? `${t(item.title)} - ${t('sidebar.tooltips.miniSiteLockedShort')}`
              : t(item.title)
            : ''
        }
      >
        <span className="icon">{item.iconStyle}</span>
        <span className="menu-label-wrap">
          <span className="menu-label">{t(item.title)}</span>
          {locked && renderLockedIcon()}
        </span>
      </div>
    );
  };

  return (
    <div ref={ref} className={`sidebar ${isOpen ? 'open' : 'collapsed'}`}>
      <div className="sidebar-brand" onClick={() => menuClick({ to: 'overview', title: 'sidebar.menu.overview' })} role="button" tabIndex={0}>
        <img src={brandMark} alt="" className="sidebar-brand__mark" />
        <span className="sidebar-brand__name">{t('sidebar.brand')}</span>
      </div>

      <div className="sidebar-content">
        <div className="sidebar-menu">
          {MenuGroups.map((group, gIndex) => (
            <div className="sidebar-group" key={gIndex}>
              <span className="sidebar-group__label">{t(group.title)}</span>
              {group.items.map((item, iIndex) => renderItem(item, `${gIndex}-${iIndex}`))}
            </div>
          ))}
        </div>

        <div
          className="sidebar-item sidebar-logout"
          onClick={() => logoutClicked()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); logoutClicked(); } }}
          title={!isOpen ? t('sidebar.menu.logout') : ''}
        >
          <span className="icon"><IoLogOut /></span>
          <span className="menu-label-wrap">
            <span className="menu-label">{t('sidebar.menu.logout')}</span>
          </span>
        </div>
      </div>
    </div>
  );
});

export default Sidebar;
